/**
 * Composable for detecting and working with the user's IANA timezone.
 *
 * Uses `Intl.DateTimeFormat` to resolve the browser's timezone
 * (e.g. "America/Bogota", "America/New_York").
 */
import type { TimezoneOption } from './TimezoneOption'

/**
 * Formats a UTC offset in minutes as ±HH:mm string.
 */
function formatOffset(offsetMinutes: number): string {
  const sign = offsetMinutes <= 0 ? '+' : '-'
  const abs = Math.abs(offsetMinutes)
  const hours = String(Math.floor(abs / 60)).padStart(2, '0')
  const minutes = String(abs % 60).padStart(2, '0')
  return `${sign}${hours}:${minutes}`
}

/**
 * Gets the current UTC offset in minutes for a given IANA timezone.
 * Returns negative values west of UTC and positive values east of UTC
 * for sorting (west → east).
 */
function getOffsetMinutes(tz: string): number {
  try {
    const now = new Date()
    // Create a formatter for the target timezone
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    })

    const parts = formatter.formatToParts(now)
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0)

    const targetMinute = Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      get('hour'),
      get('minute')
    )
    const utcMinute = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes()
    )

    return (targetMinute - utcMinute) / 60000
  } catch {
    return 0
  }
}

export function useTimezone() {
  /**
   * Returns the IANA timezone identifier detected from the browser.
   * Falls back to 'UTC' if detection is unavailable.
   */
  function getBrowserTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch {
      return 'UTC'
    }
  }

  /**
   * Formats a single IANA timezone as "(UTC±HH:mm) City/Region".
   */
  function formatTimezoneLabel(tz: string): string {
    const offset = getOffsetMinutes(tz)
    return `(UTC${formatOffset(-offset)}) ${tz.replace(/_/g, ' ')}`
  }

  /**
   * Builds the full deduplicated list of IANA timezone options,
   * sorted by UTC offset. If `detectedTz` is provided, it appears first.
   */
  function getTimezoneOptions(detectedTz?: string): TimezoneOption[] {
    let timezones: string[]
    try {
      timezones = Intl.supportedValuesOf('timeZone')
    } catch {
      // Fallback for older browsers
      timezones = [getBrowserTimezone()]
    }

    // Build options with offsets
    const seen = new Set<string>()
    const options: TimezoneOption[] = []

    for (const tz of timezones) {
      if (seen.has(tz)) continue
      seen.add(tz)

      const offsetMin = getOffsetMinutes(tz)
      options.push({
        value: tz,
        label: `(UTC${formatOffset(-offsetMin)}) ${tz.replace(/_/g, ' ')}`,
        offsetMinutes: offsetMin
      })
    }

    // Sort by offset (west → east), then alphabetically
    options.sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.value.localeCompare(b.value))

    // Pin detected timezone at the top if provided
    if (detectedTz != null && detectedTz.length > 0) {
      const idx = options.findIndex((o) => o.value === detectedTz)
      if (idx > 0) {
        const removed = options.splice(idx, 1)
        if (removed[0]) options.unshift(removed[0])
      }
    }

    return options
  }

  return {
    getBrowserTimezone,
    formatTimezoneLabel,
    getTimezoneOptions
  }
}
